import React, { useState, useEffect } from 'react';
import { couponService } from '../services/api.service';
import {
  Tag,
  Plus,
  Search,
  Filter,
  Edit2,
  Trash2,
  CheckCircle2,
  Zap,
  TrendingUp,
  Percent,
  DollarSign,
  Copy,
  Check,
  X,
  RefreshCw,
  Calendar,
  Sparkles,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const ITEMS_PER_PAGE = 10;

const BLANK_COUPON_FORM = {
  code: '',
  discount_type: 'percentage',
  discount_value: 10,
  min_spend: 30,
  max_total_uses: '',
  max_uses_per_user: 1,
  start_date: new Date().toISOString().split('T')[0],
  end_date: '',
  is_active: true
};

const Promotions = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);

  // Tab & Usage History State
  const [activeTab, setActiveTab] = useState('coupons'); // 'coupons' | 'usages'
  const [usages, setUsages] = useState([]);
  const [loadingUsages, setLoadingUsages] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(BLANK_COUPON_FORM);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const res = await couponService.getCoupons();
      setCoupons(res.coupons || []);
    } catch (err) {
      setError('Failed to load coupons from server.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsages = async () => {
    try {
      setLoadingUsages(true);
      const res = await couponService.getCouponUsages();
      setUsages(res.usages || []);
    } catch (err) {
      console.warn("Failed to load coupon usages:", err);
    } finally {
      setLoadingUsages(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
    fetchUsages();
  }, []);

  const handleCopyCode = (code) => {
  const fallbackCopy = (text) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Fallback copy failed:', err);
    }
    document.body.removeChild(textarea);
  };

  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(code).catch(() => fallbackCopy(code));
  } else {
    fallbackCopy(code);
  }

  setCopiedCode(code);
  setTimeout(() => setCopiedCode(null), 2000);
};

  // High-entropy Unique Coupon Code Generator
  const generateUniqueCode = () => {
    const prefixes = ['INFRA', 'SAVE', 'HEAL', 'GLOW', 'VIBE', 'PROMO'];
    const randomPrefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const randomChars = Math.random().toString(36).substring(2, 6).toUpperCase();
    const generated = `${randomPrefix}-${randomChars}`;
    setFormData(prev => ({ ...prev, code: generated }));
  };

  const handleOpenAddDrawer = () => {
    setEditingId(null);
    const prefixes = ['INFRA', 'SAVE', 'HEAL', 'GLOW'];
    const randomPrefix = prefixes[Math.floor(Math.random() * prefixes.length)];
    const randomChars = Math.random().toString(36).substring(2, 6).toUpperCase();

    setFormData({
      ...BLANK_COUPON_FORM,
      code: `${randomPrefix}-${randomChars}`,
      start_date: new Date().toISOString().split('T')[0]
    });
    setError('');
    setDrawerOpen(true);
  };

  const handleOpenEditDrawer = (coupon) => {
    setEditingId(coupon.id);
    setFormData({
      code: coupon.code || '',
      discount_type: coupon.discount_type || 'percentage',
      discount_value: coupon.discount_value !== undefined ? coupon.discount_value : 0,
      min_spend: coupon.min_spend !== undefined ? coupon.min_spend : 0,
      max_total_uses: coupon.max_total_uses !== null && coupon.max_total_uses !== undefined ? coupon.max_total_uses : '',
      max_uses_per_user: coupon.max_uses_per_user !== undefined ? coupon.max_uses_per_user : 1,
      start_date: coupon.start_date ? new Date(coupon.start_date).toISOString().split('T')[0] : '',
      end_date: coupon.end_date ? new Date(coupon.end_date).toISOString().split('T')[0] : '',
      is_active: Boolean(coupon.is_active)
    });
    setError('');
    setDrawerOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      if (!formData.code || !formData.code.trim()) {
        throw new Error('Coupon code is required.');
      }

      if (editingId) {
        await couponService.updateCoupon(editingId, formData);
        setSuccessMsg(`Coupon '${formData.code.toUpperCase()}' updated successfully!`);
      } else {
        await couponService.createCoupon(formData);
        setSuccessMsg(`Coupon '${formData.code.toUpperCase()}' created successfully!`);
      }

      setDrawerOpen(false);
      fetchCoupons();
      setTimeout(() => setSuccessMsg(''), 4000);
    } catch (err) {
      setError(err.message || 'Failed to save coupon.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (coupon) => {
    try {
      await couponService.updateCoupon(coupon.id, { is_active: !coupon.is_active });
      fetchCoupons();
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const handleDelete = async (coupon) => {
    if (window.confirm(`Are you sure you want to delete coupon '${coupon.code}'?`)) {
      try {
        await couponService.deleteCoupon(coupon.id);
        setSuccessMsg(`Coupon '${coupon.code}' deleted.`);
        fetchCoupons();
        setTimeout(() => setSuccessMsg(''), 3000);
      } catch (err) {
        alert('Failed to delete coupon.');
      }
    }
  };

  // Metrics Calculations
  const activeCouponsCount = coupons.filter(c => c.is_active).length;
  const totalRedemptions = coupons.reduce((sum, c) => sum + (c.current_uses_count || 0), 0);
  const percentageCouponsCount = coupons.filter(c => c.discount_type === 'percentage').length;
  const fixedCouponsCount = coupons.filter(c => c.discount_type === 'fixed_amount').length;

  // Filtering Logic
  const filteredCoupons = coupons.filter(c => {
    const matchesSearch = c.code.toLowerCase().includes(search.toLowerCase());
    const matchesType = typeFilter === 'ALL' || c.discount_type === typeFilter;
    
    let matchesStatus = true;
    const now = new Date();
    const isExpired = c.end_date && new Date(c.end_date) < now;

    if (statusFilter === 'ACTIVE') {
      matchesStatus = c.is_active && !isExpired;
    } else if (statusFilter === 'INACTIVE') {
      matchesStatus = !c.is_active;
    } else if (statusFilter === 'EXPIRED') {
      matchesStatus = isExpired;
    }

    return matchesSearch && matchesType && matchesStatus;
  });

  // Pagination Math
  const totalPages = Math.ceil(filteredCoupons.length / ITEMS_PER_PAGE) || 1;
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentCoupons = filteredCoupons.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Top Banner & Action (Matching Panels.jsx) */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' }}>
            Coupons & Promotional Offers ({coupons.length} Active Codes)
          </h2>
          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
            Create discount codes, minimum order spend thresholds (£30+), and usage limits per customer.
          </p>
        </div>

        <button onClick={handleOpenAddDrawer} className="btn-red-submit" style={{ width: 'auto', padding: '0 20px', height: '42px', fontSize: '13px' }}>
          <Plus size={18} />
          <span>Create Coupon</span>
        </button>
      </div>

      {/* Success Alert */}
      {successMsg && (
        <div className="form-alert" style={{ background: '#ecfdf5', borderColor: '#a7f3d0', color: '#047857' }}>
          <CheckCircle2 size={16} />
          <span>{successMsg}</span>
        </div>
      )}

      {/* METRICS OVERVIEW CARDS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
        
        {/* Card 1: Active Offers */}
        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Active Offers</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#ecfdf5', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Zap size={16} />
            </div>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a' }}>{activeCouponsCount}</div>
          <div style={{ fontSize: '12px', color: '#10b981', fontWeight: 700, marginTop: '4px' }}>{coupons.length} Total Codes Created</div>
        </div>

        {/* Card 2: Total Redemptions */}
        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Redemptions</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#eff6ff', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <TrendingUp size={16} />
            </div>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a' }}>{totalRedemptions}</div>
          <div style={{ fontSize: '12px', color: '#3b82f6', fontWeight: 700, marginTop: '4px' }}>Times Redeemed by Customers</div>
        </div>

        {/* Card 3: Percentage Discounts */}
        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>% OFF Discounts</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#fef2f2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Percent size={16} />
            </div>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a' }}>{percentageCouponsCount}</div>
          <div style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, marginTop: '4px' }}>Percentage-Based Coupons</div>
        </div>

        {/* Card 4: Fixed Amount Coupons */}
        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Fixed Amount (£) OFF</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#fefce8', color: '#d97706', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <DollarSign size={16} />
            </div>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a' }}>{fixedCouponsCount}</div>
          <div style={{ fontSize: '12px', color: '#d97706', fontWeight: 700, marginTop: '4px' }}>Fixed Pound Discount Codes</div>
      </div>
      </div>

      {/* NAVIGATION TABS: COUPON MANAGEMENT VS REDEMPTION USAGE HISTORY */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid #e2e8f0', paddingBottom: '2px' }}>
        <button
          onClick={() => setActiveTab('coupons')}
          style={{
            padding: '10px 18px',
            border: 'none',
            background: 'none',
            borderBottom: activeTab === 'coupons' ? '2px solid #dc2626' : '2px solid transparent',
            color: activeTab === 'coupons' ? '#dc2626' : '#64748b',
            fontWeight: activeTab === 'coupons' ? 900 : 700,
            fontSize: '13.5px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Tag size={16} />
          <span>Coupon Management</span>
        </button>

        <button
          onClick={() => { setActiveTab('usages'); fetchUsages(); }}
          style={{
            padding: '10px 18px',
            border: 'none',
            background: 'none',
            borderBottom: activeTab === 'usages' ? '2px solid #dc2626' : '2px solid transparent',
            color: activeTab === 'usages' ? '#dc2626' : '#64748b',
            fontWeight: activeTab === 'usages' ? 900 : 700,
            fontSize: '13.5px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <TrendingUp size={16} />
          <span>Redemption Usage History ({usages.length})</span>
        </button>
      </div>

      {/* TAB CONTENT A: REDEMPTION USAGE HISTORY LOGS */}
      {activeTab === 'usages' ? (
        <div className="table-card-box" style={{ padding: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' }}>
                Coupon Redemption History & Customer Tracking Log
              </h3>
              <p style={{ fontSize: '12.5px', color: '#64748b', margin: 0 }}>
                Live audit trail of all customer order redemptions, discount savings provided, and customer email tracking.
              </p>
            </div>
            <button onClick={fetchUsages} style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', padding: '8px 14px', fontSize: '12px', fontWeight: 700, color: '#334155', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <RefreshCw size={14} />
              <span>Refresh Log</span>
            </button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b', textAlign: 'left' }}>
                  <th style={{ padding: '12px 16px', fontWeight: 800 }}>PROMO CODE</th>
                  <th style={{ padding: '12px 16px', fontWeight: 800 }}>CUSTOMER / EMAIL</th>
                  <th style={{ padding: '12px 16px', fontWeight: 800 }}>ORDER REFERENCE</th>
                  <th style={{ padding: '12px 16px', fontWeight: 800, textAlign: 'right' }}>DISCOUNT GIVEN</th>
                  <th style={{ padding: '12px 16px', fontWeight: 800 }}>DATE & TIME</th>
                </tr>
              </thead>
              <tbody>
                {loadingUsages ? (
                  <tr><td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>Loading redemption logs...</td></tr>
                ) : usages.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: '#64748b' }}>No coupon redemptions recorded yet.</td></tr>
                ) : (
                  usages.map((u) => (
                    <tr key={u.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontWeight: 800, padding: '4px 10px', borderRadius: '6px', fontSize: '12px' }}>
                          {u.coupon_code}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontWeight: 800, color: '#0f172a' }}>{u.user_name || 'Customer'}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{u.user_email || u.guest_email || 'Guest User'}</div>
                      </td>
                      <td style={{ padding: '14px 16px', fontWeight: 800, color: '#0f172a' }}>
                        {u.order_number || `#ORD-${u.order_id}`}
                      </td>
                      <td style={{ padding: '14px 16px', fontWeight: 900, color: '#16a34a', textAlign: 'right' }}>
                        - £{Number(u.discount_amount || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '14px 16px', color: '#64748b', fontSize: '12px' }}>
                        {new Date(u.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* TAB CONTENT B: COUPON MANAGEMENT TABLE */
        <div className="table-card-box">
        <div className="card-header-flex" style={{ marginBottom: '16px', flexWrap: 'wrap', gap: '16px' }}>
          
          <div className="header-search-wrapper" style={{ width: '360px', display: 'block' }}>
            <input
              type="text"
              className="header-search-input"
              placeholder="Search coupon by code (e.g. INFRA-9F3K)..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            />
            <Search className="header-search-icon" size={16} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            
            {/* Type Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Filter size={14} color="#64748b" />
              <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 700 }}>Type:</span>
              <select
                className="styled-input"
                value={typeFilter}
                onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}
                style={{ padding: '6px 12px', fontSize: '12.5px', height: '36px', width: 'auto' }}
              >
                <option value="ALL">All Types</option>
                <option value="percentage">Percentage (%)</option>
                <option value="fixed_amount">Fixed Amount (£)</option>
                <option value="free_shipping">Free Shipping</option>
              </select>
            </div>

            {/* Status Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 700 }}>Status:</span>
              <select
                className="styled-input"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                style={{ padding: '6px 12px', fontSize: '12.5px', height: '36px', width: 'auto' }}
              >
                <option value="ALL">All Status</option>
                <option value="ACTIVE">Active Only</option>
                <option value="INACTIVE">Inactive Only</option>
                <option value="EXPIRED">Expired</option>
              </select>
            </div>

            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>
              Showing {filteredCoupons.length > 0 ? indexOfFirstItem + 1 : 0} - {Math.min(indexOfLastItem, filteredCoupons.length)} of {filteredCoupons.length} Coupons
            </span>

          </div>

        </div>

        {/* Coupons Data Table */}
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            <RefreshCw size={24} className="spin" style={{ marginBottom: '12px' }} />
            <div>Loading coupons database...</div>
          </div>
        ) : filteredCoupons.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>
            <Tag size={32} style={{ marginBottom: '8px', opacity: 0.4 }} />
            <div style={{ fontWeight: 700 }}>No coupons found.</div>
            <p style={{ fontSize: '12px', margin: '4px 0 0 0' }}>Click "Create Coupon" to issue a new promotional code.</p>
          </div>
        ) : (
          <>
            <table className="custom-data-table">
              <thead>
                <tr>
                  <th>Coupon Code</th>
                  <th>Discount</th>
                  <th>Min Order Spend</th>
                  <th>Usage Redemptions</th>
                  <th>Limit / Customer</th>
                  <th>Validity Window</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentCoupons.map((coupon) => {
                  const now = new Date();
                  const isExpired = coupon.end_date && new Date(coupon.end_date) < now;
                  const isMaxedOut = coupon.max_total_uses !== null && coupon.current_uses_count >= coupon.max_total_uses;

                  return (
                    <tr key={coupon.id}>
                      
                      {/* Coupon Code Column */}
                      <td>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fef2f2', border: '1px dashed #fca5a5', padding: '4px 10px', borderRadius: '6px' }}>
                          <span style={{ fontSize: '13px', fontWeight: 900, color: '#dc2626', fontFamily: 'monospace', letterSpacing: '0.05em' }}>
                            {coupon.code}
                          </span>
                          <button
                            onClick={() => handleCopyCode(coupon.code)}
                            title="Copy Code"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px', display: 'flex' }}
                          >
                            {copiedCode === coupon.code ? <Check size={13} color="#16a34a" /> : <Copy size={13} />}
                          </button>
                        </div>
                      </td>

                      {/* Discount Value */}
                      <td>
                        <div style={{ fontWeight: 800, color: '#0f172a' }}>
                          {coupon.discount_type === 'percentage' && `${Number(coupon.discount_value)}% OFF`}
                          {coupon.discount_type === 'fixed_amount' && `£${Number(coupon.discount_value).toFixed(2)} OFF`}
                          {coupon.discount_type === 'free_shipping' && `Free Shipping`}
                        </div>
                        <span style={{ fontSize: '11px', color: '#64748b', textTransform: 'capitalize' }}>
                          {coupon.discount_type.replace('_', ' ')}
                        </span>
                      </td>

                      {/* Min Order Spend */}
                      <td>
                        {Number(coupon.min_spend) > 0 ? (
                          <span style={{ fontWeight: 800, color: '#16a34a', fontSize: '13px' }}>
                            {`> £${Number(coupon.min_spend).toFixed(2)}`}
                          </span>
                        ) : (
                          <span style={{ color: '#64748b', fontSize: '12px' }}>No Minimum</span>
                        )}
                      </td>

                      {/* Total Usage Progress */}
                      <td>
                        <div style={{ fontWeight: 800, color: '#0f172a', fontSize: '13px' }}>
                          {coupon.current_uses_count} {coupon.max_total_uses !== null ? `/ ${coupon.max_total_uses}` : 'uses'}
                        </div>
                        {coupon.max_total_uses !== null && (
                          <div style={{ width: '90px', height: '5px', background: '#e2e8f0', borderRadius: '9999px', marginTop: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${Math.min(100, (coupon.current_uses_count / coupon.max_total_uses) * 100)}%`, height: '100%', background: isMaxedOut ? '#dc2626' : '#16a34a', borderRadius: '9999px' }}></div>
                          </div>
                        )}
                      </td>

                      {/* Usage Limit Per User */}
                      <td style={{ fontWeight: 700, color: '#334155', fontSize: '12.5px' }}>
                        {coupon.max_uses_per_user ? `${coupon.max_uses_per_user} per customer` : 'Unlimited'}
                      </td>

                      {/* Validity Window */}
                      <td style={{ fontSize: '12px', color: '#475569' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600 }}>
                          <Calendar size={13} color="#64748b" />
                          <span>{coupon.start_date ? new Date(coupon.start_date).toLocaleDateString('en-GB') : 'Immediate'}</span>
                          <span>→</span>
                          <span style={{ fontWeight: isExpired ? 800 : 600, color: isExpired ? '#dc2626' : '#475569' }}>
                            {coupon.end_date ? new Date(coupon.end_date).toLocaleDateString('en-GB') : 'No Expiry'}
                          </span>
                        </div>
                      </td>

                      {/* Status Toggle Badge (Matching Panels.jsx badges) */}
                      <td>
                        <button
                          onClick={() => handleToggleStatus(coupon)}
                          style={{ border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
                          title="Click to toggle status"
                        >
                          <span className={`badge-pill ${coupon.is_active && !isExpired ? 'badge-completed' : 'badge-low-stock'}`}>
                            {coupon.is_active && !isExpired ? 'Active' : (isExpired ? 'Expired' : 'Inactive')}
                          </span>
                        </button>
                      </td>

                      {/* Actions Column (Matching Panels.jsx action icon buttons) */}
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'inline-flex', gap: '6px' }}>
                          <button
                            onClick={() => handleOpenEditDrawer(coupon)}
                            title="Edit Coupon"
                            style={{ background: '#f8fafc', border: '1px solid #e2e8f0', padding: '6px', borderRadius: '6px', cursor: 'pointer', color: '#334155' }}
                          >
                            <Edit2 size={15} />
                          </button>

                          <button
                            onClick={() => handleDelete(coupon)}
                            title="Delete Coupon"
                            style={{ background: '#fef2f2', border: '1px solid #fecaca', padding: '6px', borderRadius: '6px', cursor: 'pointer', color: '#dc2626' }}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>

                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination Controls (Matching Panels.jsx) */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
                <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
                  Page {currentPage} of {totalPages}
                </span>

                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#ffffff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600 }}
                  >
                    <ChevronLeft size={16} />
                    <span>Previous</span>
                  </button>

                  {[...Array(totalPages)].map((_, idx) => (
                    <button
                      key={idx + 1}
                      onClick={() => setCurrentPage(idx + 1)}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '6px',
                        border: currentPage === idx + 1 ? '1px solid #dc2626' : '1px solid #e2e8f0',
                        background: currentPage === idx + 1 ? '#dc2626' : '#ffffff',
                        color: currentPage === idx + 1 ? '#ffffff' : '#0f172a',
                        fontWeight: 700,
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      {idx + 1}
                    </button>
                  ))}

                  <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#ffffff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600 }}
                  >
                    <span>Next</span>
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      )}

      {/* CREATE / EDIT SIDE DRAWER (Matching Panels.jsx Drawer Layout) */}
      {drawerOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)', zIndex: 999, display: 'flex', justifyContent: 'flex-end' }}>
          <div className="styled-drawer-panel" style={{ width: '100%', maxWidth: '540px', background: '#ffffff', height: '100%', overflowY: 'auto', padding: '24px', boxShadow: '-10px 0 30px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            {/* Drawer Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#0f172a', margin: '0 0 2px 0' }}>
                  {editingId ? 'Edit Coupon Code' : 'Create New Promotional Coupon'}
                </h3>
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  Set minimum order spend threshold (£30+), usage caps, and validity dates.
                </span>
              </div>

              <button
                onClick={() => setDrawerOpen(false)}
                style={{ background: '#f1f5f9', border: 'none', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={18} />
              </button>
            </div>

            {error && (
              <div className="form-alert" style={{ background: '#fef2f2', borderColor: '#fca5a5', color: '#dc2626' }}>
                <span>{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px', flex: 1 }}>
              
              {/* SECTION 1: COUPON CODE */}
              <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#dc2626', marginBottom: '12px' }}>1. Coupon Code & Discount Type</h4>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  
                  {/* Coupon Code Input + Auto Generate Button */}
                  <div>
                    <label className="input-label">Coupon Code *</label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        required
                        className="styled-input"
                        placeholder="e.g. INFRA-9F3K"
                        value={formData.code}
                        onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                        style={{ fontFamily: 'monospace', fontWeight: 800, letterSpacing: '0.05em' }}
                      />
                      <button
                        type="button"
                        onClick={generateUniqueCode}
                        style={{
                          background: '#fef2f2',
                          border: '1px solid #fca5a5',
                          borderRadius: '10px',
                          padding: '0 14px',
                          fontSize: '12px',
                          fontWeight: 800,
                          cursor: 'pointer',
                          color: '#dc2626',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        <Sparkles size={14} />
                        <span>Auto Generate</span>
                      </button>
                    </div>
                  </div>

                  {/* Discount Type & Value Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label className="input-label">Discount Type *</label>
                      <select
                        className="styled-input"
                        value={formData.discount_type}
                        onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                      >
                        <option value="percentage">Percentage Discount (%)</option>
                        <option value="fixed_amount">Fixed Amount (£) OFF</option>
                        <option value="free_shipping">Free Shipping</option>
                      </select>
                    </div>

                    {formData.discount_type !== 'free_shipping' && (
                      <div>
                        <label className="input-label">Discount Value {formData.discount_type === 'percentage' ? '(%)' : '(£)'} *</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          required
                          className="styled-input"
                          placeholder={formData.discount_type === 'percentage' ? '20' : '15.00'}
                          value={formData.discount_value}
                          onChange={(e) => setFormData({ ...formData, discount_value: e.target.value })}
                        />
                      </div>
                    )}
                  </div>

                </div>
              </div>

              {/* SECTION 2: MINIMUM SPEND & USAGE LIMITS */}
              <div style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#dc2626', marginBottom: '12px' }}>2. Minimum Spend & Usage Limits</h4>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  
                  {/* Minimum Order Spend */}
                  <div>
                    <label className="input-label">Minimum Order Spend Threshold (£)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="styled-input"
                      placeholder="30.00"
                      value={formData.min_spend}
                      onChange={(e) => setFormData({ ...formData, min_spend: e.target.value })}
                    />
                    <span style={{ fontSize: '11px', color: '#64748b', display: 'block', marginTop: '4px' }}>
                      Coupon activates when customer purchase amount is &gt; £30 (or specified value).
                    </span>
                  </div>

                  {/* Limits Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label className="input-label">Total Max Uses (All Customers)</label>
                      <input
                        type="number"
                        min="1"
                        className="styled-input"
                        placeholder="Unlimited"
                        value={formData.max_total_uses}
                        onChange={(e) => setFormData({ ...formData, max_total_uses: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="input-label">Max Uses Per Customer *</label>
                      <input
                        type="number"
                        min="1"
                        required
                        className="styled-input"
                        placeholder="1"
                        value={formData.max_uses_per_user}
                        onChange={(e) => setFormData({ ...formData, max_uses_per_user: e.target.value })}
                      />
                    </div>
                  </div>

                </div>
              </div>

              {/* SECTION 3: VALIDITY WINDOW & STATUS */}
              <div>
                <h4 style={{ fontSize: '14px', fontWeight: 800, color: '#dc2626', marginBottom: '12px' }}>3. Validity Dates & Status</h4>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <div>
                      <label className="input-label">Start Date</label>
                      <input
                        type="date"
                        className="styled-input"
                        value={formData.start_date}
                        onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      />
                    </div>

                    <div>
                      <label className="input-label">Expiry / End Date</label>
                      <input
                        type="date"
                        className="styled-input"
                        value={formData.end_date}
                        onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Active Toggle Checkbox */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                    <input
                      type="checkbox"
                      id="promotions_active_check"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      style={{ width: '18px', height: '18px', accentColor: '#dc2626', cursor: 'pointer' }}
                    />
                    <label htmlFor="promotions_active_check" style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a', cursor: 'pointer' }}>
                      Enable coupon immediately upon saving (Active)
                    </label>
                  </div>

                </div>
              </div>

              {/* Drawer Submit Buttons (Matching Panels.jsx) */}
              <div style={{ marginTop: 'auto', paddingTop: '16px', display: 'flex', gap: '12px' }}>
                <button
                  type="button"
                  onClick={() => setDrawerOpen(false)}
                  className="btn-secondary"
                  style={{ flex: 1 }}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-red-submit"
                  style={{ flex: 2, opacity: submitting ? 0.7 : 1 }}
                >
                  {submitting ? 'Saving...' : (editingId ? 'Update Coupon' : 'Create Coupon')}
                </button>
              </div>

            </form>

          </div>
        </div>
      )}

    </div>
  );
};

export default Promotions;
